import React, { ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: undefined,
      errorInfo: undefined
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by boundary:', {
      error,
      errorInfo
    });

    this.setState({
      error,
      errorInfo
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <h2 className="text-lg font-semibold text-red-700">
              Something went wrong
            </h2>
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-2 p-4 bg-red-100 rounded">
                <p className="text-red-800 font-mono text-sm">
                  {this.state.error?.toString()}
                </p>
                <pre className="mt-2 text-xs text-red-700 overflow-auto">
                  {this.state.errorInfo?.componentStack}
                </pre>
              </div>
            )}
            <button
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </button>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

export const useErrorBoundary = () => {
  const [hasError, setHasError] = React.useState<boolean>(false);

  const showBoundary = React.useCallback((error: Error) => {
    console.error('Error caught by hook:', error);
    setHasError(true);
  }, []);

  return { hasError, showBoundary };
};
